import {
  BulbOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  LineChartOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { dbCols, exposureInitialValues, UserRole } from '@cc/schema';
import { Create, EditButton, SaveButton } from '@refinedev/antd';

import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd';

import {
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useGetLocale,
  useWarnAboutChange,
} from '@refinedev/core';

import * as Sentry from '@sentry/react';
import { useActor } from '@xstate/react';
import { useWatch } from 'antd/lib/form/Form';
import qs from 'qs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useWindowSize } from 'rooks';
import { TherapistActivityReactContext } from '../../App';
import { SelectClient } from '../../components/SelectClient';
import { useCcEditableTable } from '../../helpers';
import { useClientRoster } from '../../hooks/client-options';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { useClientEra } from '../../hooks/useClientEra';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { IUser } from '../../providers';
import { Exposure, ExposureStimulus } from '../../schema/be-graphql-generated';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import {
  canEditExposure,
  COEXISTENCE_COPY_KEYS,
  deleteWindowOpen,
  EXPOSURE_DOMAIN,
  exposureEra,
  isRefusedAnswer,
  refusedAnswerError,
} from './esHelpers';
import useStyles from './styles.style';
import { UpdateExposureStimulusDocument } from '../../schema/fe-graphql-generated';
import type { DocumentNode } from 'graphql';

const { Title } = Typography;

export const ExposureCreate = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const { styles } = useStyles();

  const getLocale = useGetLocale();

  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  // the WHOLE practice roster (P011) — the stimulus edit gate below reads the selected client off it
  const { clients, clientsLoading } = useClientRoster();

  const { setWarnWhen } = useWarnAboutChange();

  /**
   * Report a failed write to the therapist, in three tiers (the E02 rule):
   *   1. a COEXISTENCE code, re-voiced — the server's copy addresses the account owner;
   *   2. any other coded rejection — the server's own localized message, verbatim;
   *   3. anything else — the tool's generic save error, and Sentry.
   */
  const reportFailure = useCallback(
    (error: unknown, fallbackKey = 'shared.exposure.saveError') => {
      const { code, message: serverMessage } = graphqlErrorInfo(error);
      const therapistCopyKey = code ? COEXISTENCE_COPY_KEYS[code] : undefined;
      if (therapistCopyKey) {
        message.error(t(therapistCopyKey));
        return;
      }
      if (code && serverMessage) {
        message.error(serverMessage);
        return;
      }
      message.error(t(fallbackKey));
      Sentry.captureException(error);
    },
    [t],
  );

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  const { clientId: locationClientId, exposureId: savedExposureId } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};

  const { innerWidth } = useWindowSize();

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (locationClientId as string) ||
      therapistActivityState.context.filterClientId,
  );

  const [form] = Form.useForm<typeof exposureInitialValues>();

  const [isGenerating, setIsGenerating] = useState(false);
  const type = useWatch('type', form);
  const fear = useWatch('fear', form);
  const obsession = useWatch('obsession', form);

  const [exposureId, setExposureId] = useState(
    savedExposureId as string | undefined,
  );

  useActivityTracking(exposureId ? exposureId : `${dbCols.exposures}/create`);

  /**
   * The CLIENT's era for this domain — never the therapist's (plan 07 C3: the roster is MIXED). It
   * is re-asked on every mount and after a coexistence refusal, because the era is a server fact
   * that can change while the page is open (T112, the flip that stranded the E12 pilot).
   */
  const { isEsClient, refetchEra } = useClientEra(clientId, EXPOSURE_DOMAIN);

  const {
    mutate: generateGptExposuresHierarchy,
    data: generateGptExposuresHierarchyData,
    isLoading: generateGptExposuresHierarchyLoading,
    error: generateGptExposuresHierarchyError,
  } = useCustomMutation<{
    response: ExposureStimulus[] | null;
    code?: string | null;
  }>();

  useEffect(() => {
    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
    }
  }, [clientId]);

  /**
   * The generation answered. A ladder refetches the table; NO ladder now says WHY.
   *
   * The shipped code treated every answer as a success — it stopped the spinner and refetched, so a
   * refused or rate-limited generation looked exactly like one that produced nothing, and the
   * therapist was left staring at an unchanged table. The E-era lane records
   * `ExposureHierarchyGenerationFailed{code}` for each case and the payload now carries that code
   * (additive, read-only), which is what the board means by "the portal shows the generation error
   * and re-enables Generate".
   */
  useEffect(() => {
    if (
      !generateGptExposuresHierarchyData ||
      generateGptExposuresHierarchyLoading
    ) {
      return;
    }
    setIsGenerating(false);
    const answer = generateGptExposuresHierarchyData.data;
    const rungs = Array.isArray(answer?.response) ? answer.response : [];
    if (rungs.length > 0) {
      message.success(t('pages.exposure.hierarchyGenerated'));
      tableQueryResult.refetch();
      return;
    }
    const code = answer?.code ?? '';
    message.error(
      t(
        code === 'CONTENT_FILTERED'
          ? 'pages.exposure.hierarchyContentFiltered'
          : code === 'RATE_LIMITED'
          ? 'pages.exposure.hierarchyRateLimited'
          : 'pages.exposure.hierarchyGenerationFailed',
      ),
    );
    // the legacy path can also answer `{response: null}` for a permission miss; refetching is
    // harmless and keeps the table honest either way
    tableQueryResult.refetch();
  }, [generateGptExposuresHierarchyLoading, generateGptExposuresHierarchyData]);

  useEffect(() => {
    if (!generateGptExposuresHierarchyError) return;
    setIsGenerating(false);
    reportFailure(
      generateGptExposuresHierarchyError,
      'pages.exposure.hierarchyGenerationFailed',
    );
    // a coexistence refusal means the era this page loaded with is stale (T112) — re-ask it
    void refetchEra();
  }, [generateGptExposuresHierarchyError]);

  const onGenerateHypothesis = async () => {
    const values = form.getFieldsValue();
    const requestData = {
      lng: currentLocale?.startsWith('ru') ? 'ru' : 'en',
      type: values.type,
      safetyBehaviorsToAvoid: values.safetyBehaviorsToAvoid,
      fear: values.type === 0 ? values.fear : values.obsession,
    };

    setIsGenerating(true);
    try {
      generateGptExposuresHierarchy({
        url: '',
        method: 'post',
        values: {},
        // the outcome is reported by the effects above — never the raw GraphQL error toast
        errorNotification: false,
        meta: {
          operation: 'generateGptExposuresHierarchy',
          // `code` is E21's additive read-only field: WHY there is no ladder, when there is none
          fields: ['response', 'code'],
          variables: {
            clientId: {
              name: 'clientId',
              type: 'ID',
              value: clientId,
            },
            fear: {
              name: 'fear',
              type: 'String!',
              value: requestData?.fear,
            },
            lng: {
              name: 'lng',
              type: 'String!',
              value: requestData?.lng,
            },
            type: {
              name: 'type',
              type: 'Int!',
              value: requestData?.type,
            },
            exposureId: {
              name: 'exposureId',
              type: 'ID!',
              value: exposureId,
            },
            safetyBehaviorsToAvoid: {
              name: 'safetyBehaviorsToAvoid',
              type: 'String',
              value: requestData?.safetyBehaviorsToAvoid,
            },
          },
        },
      });
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  const memoizedTypeOptions = useMemo(() => {
    return t('shared.exposure.types', {
      returnObjects: true,
      // @ts-ignore
    }).map((type: any, index: number) => ({
      label: type.name,
      value: index,
      description: type.description,
    }));
  }, [i18n.language]);

  const memoizedAvoidanceOptions = useMemo(() => {
    return t('shared.exposure.avoidances', {
      returnObjects: true,
      // @ts-ignore
    }).map((avoidance: string, index: number) => (
      <Select.Option
        key={index}
        value={index}
        data-testid="cc-exposure-situation-avoid-option"
      >
        {avoidance}
      </Select.Option>
    ));
  }, [i18n.language]);

  const {
    data: exposureData,
    isLoading: exposureLoading,
    refetch: refetchExposure,
    error: exposureError,
  } = useCustom<Exposure>({
    url: '',
    method: 'get',
    meta: {
      operation: 'exposure',
      fields: [
        '_id',
        // THE ROW DECIDES THE ERA (T95a): the ES identity the projection stamps is what says
        // whether this ladder is log-owned, and therefore whether an ES-only control may be shown
        'exposureId',
        'userId',
        'type',
        'fear',
        'obsession',
        'safetyBehaviorsToAvoid',
        {
          hierarchy: [
            '_id',
            'exposureStimulusId',
            'createdAt',
            'rank',
            'title',
            'description',
            'exposureId',
            'imageUrls',
            'imagePrompt',
            'sud',
            'avoidance',
            'worstThingThatCouldHappen',
            {
              expositions: [
                'createdAt',
                'anticipatedSud',
                'reactionNotes',
                {
                  suds: ['sud', 'reactedAt'],
                },
              ],
            },
          ],
        },
      ],

      variables: {
        _id: {
          name: '_id',
          type: 'ID',
          value: exposureId,
        },
      },
    },
    /**
     * THE ROW'S ERA IS A SERVER FACT, ASKED FRESH (T112 — the same rule `useClientEra` follows and
     * the E12/E02 pages already apply to their own row query).
     *
     * Half of `useEsPath` is the CLIENT's mode and half is THIS ROW's ES identity, and only the
     * first half was being re-asked. Served from cache, the ladder kept the identity it had before
     * a flip converted it, so the page went on rendering `data-era="legacy"` and hiding the E-era
     * affordances while its writes were already being routed to the log — a page telling the
     * therapist one thing while the server did another, until they happened to reload.
     */
    queryOptions: {
      enabled: Boolean(exposureId),
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });

  useEffect(() => {
    if (exposureData?.data) {
      const {
        __typename,
        _id,
        _key,
        _rev,
        createdAt,
        updatedAt,
        hierarchy,
        ...rest
      } = exposureData?.data;
      form?.setFieldsValue({
        ...rest,
        type: rest?.type || 0,
      });
      if (exposureData?.data.userId) {
        setClientId(exposureData?.data.userId);
      }

      if (exposureData?.data?._id && exposureData?.data?.userId) {
        therapistActivitySend({
          type: 'ENTITY_CLIENT_ID_CHANGED',
          clientId: exposureData?.data?.userId,
        });
      }
    }
  }, [exposureData]);

  const {
    mutate: upsertExposure,
    data: upsertExposureData,
    isLoading: upsertExposureLoading,
    error: upsertExposureError,
  } = useCustomMutation<Exposure>();

  const saveData = useCallback(
    async (values?: typeof exposureInitialValues) => {
      const data = values || form?.getFieldsValue();

      if (!clientId) {
        return;
      }

      /**
       * A `null` ANSWER IS A REFUSAL, NEVER A SAVE (review 2026-09-05, D6). `upsertExposure`
       * answers a silent null when the caller may not act for this client — the legacy contract,
       * deliberately kept (founder G1 2026-08-28: the domain can see neither a role nor a grant) —
       * and refine's `onSuccess` fires on `data: null` exactly as it does on a row. This promise
       * used to resolve, "Exposure saved" appeared, `dirty` was cleared and the unsaved-changes
       * guard came down, over a write that never happened. Rejecting sends it through
       * `reportFailure`: the therapist is told, the work stays on screen, Save stays offered.
       */
      await new Promise<void>((resolve, reject) => {
        upsertExposure(
          {
            url: '',
            method: 'post',
            values: {},
            meta: {
              operation: 'upsertExposure',
              fields: ['_id'],
              variables: {
                input: {
                  name: 'input',
                  type: 'ExposureInput',
                  value: {
                    data,
                    where: {
                      clientId,
                      _id: exposureId,
                    },
                  },
                },
              },
            },
            // the outcome is reported below — never the raw GraphQL error toast
            errorNotification: false,
          },
          {
            onSuccess: (res) =>
              isRefusedAnswer(res?.data)
                ? reject(refusedAnswerError('upsertExposure'))
                : resolve(),
            onError: (e) => reject(e),
          },
        );
      });

      message.success(t('shared.exposure.saveSuccess'));
    },
    [clientId, exposureId, t, upsertExposure, form],
  );

  /**
   * EXPLICIT SAVE (T99 (f) — the single named blocker to `exposure` entering `pilotDomains`).
   *
   * This form used to autosave `onValuesChange` on a 700 ms debounce with no Save button. Against
   * an event-sourced backend that mints a COMMAND per keystroke burst, each one carrying the whole
   * clinical text, so the stream would record a therapist's typing instead of their decisions. The
   * deciders' idempotence softens that (a save that changes nothing records nothing) — it does not
   * remove it, because every pause between words genuinely changes the text.
   *
   * IT IS CONVERTED FOR BOTH ERAS, NOT ONLY THE ES PATH, and that is a judgment call worth reading.
   * The brief allowed the narrower fix. Three things argue against it: (1) the client is chosen from
   * a DROPDOWN on this very page and a therapist's roster is MIXED (plan 07 C3), so an era-gated
   * autosave would silently change the save model mid-draft when they switch client — the worst
   * possible moment to change where a keystroke goes; (2) T99 (f) says the four autosaving tools
   * "must be converted to an explicit Save BEFORE each one's domain is event-sourced", and the
   * recipe it points at (`downwardArrow/create.tsx`, founder-approved 2026-09-01) converted the
   * whole tool; (3) two of the three defects the autosave caused there were LEGACY bugs — the
   * duplicate-insert race is live in this file too (`exposureId` is only set when the first save's
   * response returns). The legacy behaviour change is named in the nightly log for the founder.
   *
   * `UnsavedChangesNotifier` (mounted in App.tsx) warns on navigation, so nothing is lost by not
   * writing every keystroke.
   */
  const [dirty, setDirty] = useState(false);

  /**
   * The era of THIS ladder. `useEsPath` needs BOTH the client's mode and the row's identity: an
   * `es` client's exposure that the converter has not reached is still a legacy row, and offering
   * an ES-only control on it would be offering a button the server refuses (T95a).
   */
  const { useEsPath, unconvertedRow, era } = exposureEra({
    isEsClient,
    row: exposureData?.data ?? null,
    rowLoaded: !!exposureId ? exposureData !== undefined : true,
  });

  const onValuesChange = useCallback(() => {
    setDirty(true);
    setWarnWhen(true);
  }, [setWarnWhen]);

  const onSave = useCallback(async () => {
    try {
      await saveData();
    } catch (e) {
      // the work is still unsaved: keep the guard up and leave Save offered
      reportFailure(e);
      return;
    }
    setDirty(false);
    setWarnWhen(false); // saved — leaving is safe again
    /**
     * REFRESH THE ERA AFTER A WRITE, NOT ONLY AFTER A REFUSAL (T112's own rule — the E12 legacy
     * save paths "re-read the row's identity … and refresh the signal AFTER the write").
     *
     * A write is the moment the row can CROSS: the first ES-path save mints the ladder's identity,
     * and a conversion the operator ran while this page was open lands on the very same row. Only
     * the refused-generation path re-asked, so an ordinary Save left the page believing it was on
     * the legacy side of a seam it had already stepped over.
     */
    await Promise.all([
      refetchEra(),
      // the FIRST save has no row id in state yet — its own effect loads the row it just created
      ...(exposureId ? [refetchExposure()] : []),
    ]);
  }, [
    saveData,
    setWarnWhen,
    reportFailure,
    refetchEra,
    refetchExposure,
    exposureId,
  ]);

  const {
    mutate: upsertExposureStimulus,
    data: upsertExposureStimulusData,
    isLoading: upsertExposureStimulusLoading,
    error: upsertExposureStimulusError,
  } = useCustomMutation<{ exposureStimulus: ExposureStimulus }>();

  useEffect(() => {
    if (exposureData?.data?._id) {
      setExposureId(exposureData?.data?._id);
    }
  }, [exposureData]);

  useEffect(() => {
    if (upsertExposureData?.data?._id) {
      if (!exposureId) {
        therapistActivitySend({
          type: 'CREATED',
          resource: upsertExposureData?.data?._id,
        });
        setExposureId(upsertExposureData?.data?._id);
      } else {
        therapistActivitySend({
          type: 'UPDATED',
          resource: upsertExposureData?.data?._id,
        });
      }
    }
  }, [upsertExposureData, exposureId]);

  const {
    table: { tableProps, tableQueryResult },
    form: { formProps },
    isEditing,
    saveButtonProps,
    cancelButtonProps,
    editButtonProps,
  } = useCcEditableTable<ExposureStimulus>(
    {
      queryOptions: {
        select: (data) => {
          // @ts-ignore
          return { data: data.data.data, total: data.total };
        },
      },
      sorters: {
        initial: [
          {
            field: 'rank',
            order: 'asc',
          },
        ],
      },
      filters: {
        permanent: [
          {
            field: 'exposureId',
            operator: 'eq',
            value: exposureId,
          },
        ],
      },

      meta: {
        operation: 'exposureStimuli',
        fields: [
          {
            data: [
              '_id',
              'exposureStimulusId',
              'createdAt',
              'rank',
              'title',
              'exposureId',
              'description',
              'imageUrls',
              'imagePrompt',
              'sud',
              'avoidance',
              'worstThingThatCouldHappen',
              {
                expositions: [
                  'createdAt',
                  'anticipatedSud',
                  'reactionNotes',
                  {
                    suds: ['sud', 'reactedAt'],
                  },
                ],
              },
            ],
          },
          'count',
        ],
      },
    },
    {
      resource: 'exposureStimulus',
      onMutationSuccess: () => {
        tableQueryResult.refetch();
        cancelButtonProps?.onClick();
      },
      meta: {
        operation: 'exposureStimulus',
        fields: [
          '_id',
          'exposureStimulusId',
          'createdAt',
          'rank',
          'title',
          'description',
          'imageUrls',
          'imagePrompt',

          'exposureId',
          'sud',
          'avoidance',
          'worstThingThatCouldHappen',
          {
            expositions: [
              'createdAt',
              'anticipatedSud',
              'reactionNotes',
              {
                suds: ['sud', 'reactedAt'],
              },
            ],
          },
        ],
      },
      mutationMeta: {
        operation: 'updateExposureStimulus',
        gqlMutation:
          UpdateExposureStimulusDocument as unknown as import('graphql').DocumentNode,
        fields: [
          '_id',
          'exposureStimulusId',
          'createdAt',
          'rank',
          'title',
          'description',
          'imageUrls',
          'imagePrompt',

          'exposureId',
          'sud',
          'avoidance',
          'worstThingThatCouldHappen',
          {
            expositions: [
              'createdAt',
              'anticipatedSud',
              'reactionNotes',
              {
                suds: ['sud', 'reactedAt'],
              },
            ],
          },
        ],
        variables: {
          input: {
            type: 'updateExposureInput',
          },
        },
      },
    },
  );

  const createExposureStimulus = useCallback(
    async (values?: ExposureStimulus) => {
      if (!clientId || !exposureId) {
        return;
      }

      const data = {
        exposureId,
        expositions: [],
        rank: (tableProps.dataSource || []).length,
      };

      upsertExposureStimulus(
        {
          url: '',
          method: 'post',
          values: {},
          meta: {
            operation: 'upsertExposureStimulus',
            fields: ['_id'],
            variables: {
              input: {
                name: 'input',
                type: 'ExposureStimulusInput',
                value: {
                  data,
                  where: {
                    clientId,
                    exposureId,
                    _id: values?._id || null,
                  },
                },
              },
            },
          },
          errorNotification: false,
        },
        {
          // A `null` ANSWER IS A REFUSAL (D6), and this hall of the same rule: the shipped code
          // toasted "saved" before the mutation had answered at all, and then — once it did await —
          // still toasted it when the server answered nothing.
          onSuccess: (res) =>
            isRefusedAnswer(res?.data)
              ? reportFailure(refusedAnswerError('upsertExposureStimulus'))
              : message.success(
                  t('shared.exposure.situationOrStimulusSaveSuccess'),
                ),
          onError: (e) => reportFailure(e),
        },
      );
    },
    [clientId, exposureId, t, (tableProps.dataSource || []).length],
  );

  useEffect(() => {
    if (!upsertExposureLoading && upsertExposureStimulusData?.data) {
      setTimeout(async () => {
        const newRes = await tableQueryResult.refetch();
        if (newRes?.data?.data.length) {
          const newStimulus = newRes?.data?.data[newRes?.data?.data.length - 1];
          if (newStimulus) {
            // @ts-ignore
            editButtonProps?.(newStimulus._id)?.onClick();
          }
        }
      }, 100);
    }
  }, [upsertExposureStimulusData, upsertExposureLoading]);

  const {
    mutate: mutateUpdateExposureStimuli,
    data: updateExposureStimuliData,
    isLoading: updateExposureStimuliLoading,
    error: updateExposureStimuliError,
  } = useCustomMutation<{ exposureStimuli: ExposureStimulus[] }>();

  const updateExposureStimuli = useCallback(
    async (values?: ExposureStimulus[]) => {
      if (!clientId || !values?.length) {
        return;
      }

      // Note: Add if more is needed
      const updateValues = values.map((value, index) => {
        const { _id, rank, exposureId, avoidance } = value;

        return {
          _id,
          rank,
          exposureId,
          avoidance,
        };
      });

      mutateUpdateExposureStimuli(
        {
          url: '',
          method: 'post',
          values: {},
          meta: {
            operation: 'updateExposureStimuli',
            fields: ['_id'],
            variables: {
              input: {
                name: 'input',
                type: 'UpdateExposureStimuliInput',
                value: {
                  data: updateValues,
                  where: {
                    clientId,
                  },
                },
              },
            },
          },
          errorNotification: false,
        },
        {
          onSuccess: (res) => {
            // a `null` answer is a REFUSAL (D6) — and here the ranks on screen are then a guess,
            // so it takes the same re-read the error path does
            if (isRefusedAnswer(res?.data)) {
              reportFailure(refusedAnswerError('updateExposureStimuli'));
            } else {
              message.success(
                t('shared.exposure.situationOrStimulusSaveSuccess'),
              );
            }
            setTimeout(() => tableQueryResult.refetch(), 100);
          },
          onError: (e) => {
            reportFailure(e);
            // the ranks in the table are now a guess — re-read what the server actually holds
            setTimeout(() => tableQueryResult.refetch(), 100);
          },
        },
      );
    },
    [clientId, exposureId, t, (tableProps.dataSource || []).length],
  );

  useEffect(() => {
    // @ts-ignore
    if (updateExposureStimuliData?.data.length && exposureId) {
      therapistActivitySend({
        type: 'UPDATED',
        resource: exposureId,
      });
    }
  }, [updateExposureStimuliData]);

  const {
    mutate: deleteExposureStimulus,
    data: deleteExposureStimulusData,
    isLoading: deleteExposureStimulusLoading,
    error: deleteExposureStimulusError,
  } = useCustomMutation<{ exposureStimulus: ExposureStimulus }>();

  const onDeleteExposureStimulus = useCallback(
    async (_id: string) => {
      if (!clientId || !_id) {
        return;
      }

      deleteExposureStimulus(
        {
          url: '',
          method: 'post',
          values: {},
          meta: {
            operation: 'deleteExposureStimulus',
            fields: ['_id'],
            variables: {
              input: {
                name: 'input',
                type: 'deleteExposureStimulusInput',
                value: {
                  where: {
                    id: _id,
                  },
                },
              },
            },
          },
          errorNotification: false,
        },
        {
          onSuccess: (res) => {
            // a `null` answer is a REFUSAL (D6). Here it is also the SHIPPED way the server says
            // "the 2-hour delete window has closed", so a "deleted" toast over it was the most
            // misleading of the four: the rung is still there, and the re-read below shows it.
            if (isRefusedAnswer(res?.data)) {
              reportFailure(
                refusedAnswerError('deleteExposureStimulus'),
                'shared.exposure.deleteError',
              );
            } else {
              message.success(
                t('shared.exposure.situationOrStimulusSaveSuccess'),
              );
            }
            // the delete re-ranks the survivors on the server (its own second append); the table
            // has to re-read them or the next reorder would send stale ranks
            setTimeout(() => tableQueryResult.refetch(), 100);
          },
          onError: (e) => {
            reportFailure(e, 'shared.exposure.deleteError');
            setTimeout(() => tableQueryResult.refetch(), 100);
          },
        },
      );
    },
    [clientId, exposureId, t, (tableProps.dataSource || []).length],
  );

  useEffect(() => {
    // @ts-ignore
    if (!!deleteExposureStimulusData?.data && exposureId) {
      therapistActivitySend({
        type: 'UPDATED',
        resource: exposureId,
      });
    }
  }, [deleteExposureStimulusData]);

  const moveExposureStimuli = async (index: number, newIndex: number) => {
    const dataSource = [...(tableProps.dataSource || [])];

    const item = dataSource[index];
    const newDataSource = dataSource.filter((_, i) => i !== index);
    newDataSource.splice(newIndex, 0, item);

    const newValues = newDataSource.map((item, index) => ({
      ...item,
      rank: index,
    }));

    await updateExposureStimuli(newValues);
  };

  return (
    <Create
      title={<>{t('shared.exposure.title')}</>}
      isLoading={clientsLoading}
      saveButtonProps={{
        'onClick': onSave,
        'loading': upsertExposureLoading,
        // nothing to save is not a thing to offer: the button follows the dirty flag that also
        // drives the leave-warning, so "enabled" and "you would lose work" mean the same thing.
        //
        // `exposureLoading` counts ONLY when there is a row to load. The row query is `enabled:
        // Boolean(exposureId)`, and react-query reports a DISABLED query as `status: 'loading'`
        // for ever — so on the create-a-new-ladder path (`?clientId=…`, no row) the raw flag is
        // permanently true and would keep Save disabled no matter how much the therapist typed.
        'disabled':
          !clientId ||
          !dirty ||
          isGenerating ||
          (!!exposureId && exposureLoading) ||
          generateGptExposuresHierarchyLoading,
        'children': t('pages.exposure.save'),
        'data-testid': 'cc-exposure--save',
      }}
    >
      <div className={styles['exposure-container']}>
        <SelectClient clientId={clientId} setClientId={setClientId} />
      </div>

      {unconvertedRow && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="cc-exposure-unconverted"
          message={t('pages.exposure.unconvertedRow')}
        />
      )}

      <Form<typeof exposureInitialValues>
        form={form}
        initialValues={{
          ...exposureInitialValues,
        }}
        onValuesChange={onValuesChange}
        onFinish={(values) => {}}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 16 }}
        labelWrap
        data-testid="cc-create-exposure-form"
        data-era={era}
        data-es-row={useEsPath ? 'true' : 'false'}
      >
        <Form.Item
          name="type"
          label={t('shared.exposure.type')}
          rules={[
            {
              required: true,
              message: t('shared.exposure.typeRequired'),
            },
          ]}
          style={{
            display: exposureId || !clientId ? 'none' : 'block',
          }}
        >
          <Radio.Group>
            {memoizedTypeOptions.map((option: any, idx: number) => (
              <Radio
                key={option.value}
                value={option.value}
                data-testid={`cc-create-exposure-type-radio-${idx}`}
              >
                {option.label}{' '}
                <Tooltip
                  title={option.description}
                  overlayClassName={styles['exposure--tooltip']}
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        {type === null ? null : type === 0 ? (
          <Form.Item name={'fear'} label={t('shared.exposure.fear')}>
            <Input.TextArea
              data-testid="cc-create-exposure-fear"
              placeholder={t('shared.exposure.fearPlaceholder')}
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={!exposureId && upsertExposureLoading}
            />
          </Form.Item>
        ) : (
          <div>
            <Form.Item
              name={'obsession'}
              label={t('shared.exposure.obsession')}
            >
              <Input.TextArea
                data-testid="cc-create-exposure-obsession"
                placeholder={t('shared.exposure.obsessionPlaceholder')}
                autoSize={{ minRows: 2, maxRows: 6 }}
                disabled={!exposureId && upsertExposureLoading}
              />
            </Form.Item>

            <Form.Item
              name={'safetyBehaviorsToAvoid'}
              label={t('shared.exposure.compulsion')}
              tooltip={t('shared.exposure.compulsionTip')}
            >
              <Input.TextArea
                data-testid="cc-create-exposure-compulsion"
                placeholder={t('shared.exposure.compulsion')}
                autoSize={{ minRows: 2, maxRows: 6 }}
                disabled={!exposureId && upsertExposureLoading}
              />
            </Form.Item>
          </div>
        )}

        {type !== null && (fear || obsession) && (
          <div>
            <Divider />

            <Title level={5}>
              {t('shared.exposure.situationsOrStimuliHierarchy')}:
            </Title>

            {/*
              BOTH of these need a SAVED exposure to hang off — `createExposureStimulus` needs its
              parent's `_id` and `generateGptExposuresHierarchy` takes `exposureId: ID!`. The
              autosave used to supply one as a side effect of typing; with an explicit Save the
              therapist has to press it first, and the shipped copy for exactly that
              (`saveBeforeSituation`) already existed — it was simply unreachable.
            */}
            <div className={styles['exposures--generate-row']}>
              <Tooltip
                title={
                  exposureId ? '' : t('shared.exposure.saveBeforeSituation')
                }
              >
                <Button
                  onClick={() => {
                    createExposureStimulus();
                  }}
                  disabled={
                    !exposureId || isGenerating || upsertExposureStimulusLoading
                  }
                  data-testid="cc-create-exposure-add-situation-or-stimulus"
                  className={styles['exposures--generate-row-button']}
                >
                  <PlusOutlined /> {t('shared.exposure.addSituationOrStimuli')}
                </Button>
              </Tooltip>
              {/*
                WHY IT IS OFF, NOT JUST THAT IT IS (founder, portal testing 2026-09-10: "we cannot
                generate hierarchy before we click save on the fear, which makes sense, but is not
                intuitive — we need some hint when it is disabled").
                A tooltip already named the save rule, but it could never appear: antd renders the
                trigger as a DISABLED button, and a disabled button emits no mouse events, so the
                Tooltip never received a hover. Wrapping it in a plain span gives the tooltip an
                element that does — the shape antd's own docs prescribe for this — and the title now
                names whichever reason actually applies rather than only the first.
              */}
              <Tooltip
                title={
                  !exposureId
                    ? t('shared.exposure.saveBeforeSituation')
                    : isGenerating
                    ? t('shared.exposure.generatingInProgress')
                    : (tableProps.dataSource?.length || 0) > 1
                    ? t('shared.exposure.hierarchyAlreadyBuilt')
                    : ''
                }
              >
                <span
                  data-testid="cc-create-exposure-generate-hierarchy-wrap"
                  style={{ display: 'inline-block', cursor: 'not-allowed' }}
                >
                  <Button
                    className={styles['exposures--generate-row-button']}
                    data-testid="cc-create-exposure-generate-hierarchy"
                    type="primary"
                    onClick={onGenerateHypothesis}
                    icon={<BulbOutlined />}
                    disabled={
                      !exposureId ||
                      isGenerating ||
                      (tableProps.dataSource?.length || 0) > 1
                    }
                  >
                    {t('shared.exposure.generateHierarchy')}
                  </Button>
                </span>
              </Tooltip>
            </div>
            <div className={styles['exposures--is-generating-row']}>
              {isGenerating && (
                <Spin
                  style={{
                    margin: '20px 0',
                  }}
                  size="large"
                  tip={t('shared.behaviorExperiment.generating')}
                />
              )}
            </div>
          </div>
        )}
      </Form>

      {type !== null && (fear || obsession) && (
        <Form {...formProps}>
          <Table
            {...tableProps}
            rowKey="_id"
            onChange={(pagination, filters, sorter, extra) => {
              // Note: The data we get from Ant.design filters does not match the
              // react refine one. Currently it works fine if I simply silence it the way below.
              // TOOD: Find a way to convert the data to match the react refine one and fix it better
              // @ts-ignore
              tableProps?.onChange?.(pagination, null, sorter, extra);
            }}
            pagination={false}
            data-testid="cc-create-exposure-table"
          >
            <Table.Column<ExposureStimulus>
              key="rank"
              dataIndex={'rank'}
              title={t('shared.exposure.rank')}
              width={40}
              render={(value, record) => {
                return (
                  <div className={styles['exposures--move-row']}>
                    <div>{`${(record.rank || 0) + 1}. `}</div>
                    <div className={styles['exposures--move-buttons']}>
                      <Tooltip title={t('common.moveUp')}>
                        <Button
                          data-testid={`cc-create-exposure-move-up`}
                          icon={<CaretUpOutlined />}
                          disabled={
                            record.rank === 0 || updateExposureStimuliLoading
                          }
                          onClick={() => {
                            const newIndex = (record.rank || 0) - 1;
                            if (newIndex >= 0) {
                              moveExposureStimuli(record.rank || 0, newIndex);
                            }
                          }}
                        />
                      </Tooltip>
                      <Tooltip title={t('common.moveDown')}>
                        <Button
                          data-testid={`cc-create-exposure-move-down`}
                          icon={<CaretDownOutlined />}
                          disabled={
                            record.rank ===
                              (tableProps.dataSource || []).length - 1 ||
                            updateExposureStimuliLoading
                          }
                          onClick={() => {
                            const newIndex = (record.rank || 0) + 1;
                            if (
                              newIndex < (tableProps.dataSource || []).length
                            ) {
                              moveExposureStimuli(record.rank || 0, newIndex);
                            }
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                );
              }}
            />

            <Table.Column<ExposureStimulus>
              key="title"
              dataIndex="title"
              title={t('shared.exposure.situationOrStimulusTitle')}
              width={'30%'}
              render={(value, record) => {
                if (isEditing(record._id)) {
                  return (
                    <Form.Item
                      name={['title']}
                      style={{ margin: 0, minWidth: 120 }}
                    >
                      <Input.TextArea
                        data-testid={`cc-create-exposure-title`}
                        placeholder={t(
                          'shared.exposure.situationOrStimulusTitle',
                        )}
                        autoSize={{ minRows: 4 }}
                      />
                    </Form.Item>
                  );
                }
                return value;
              }}
            />

            <Table.Column<ExposureStimulus>
              key="description"
              dataIndex="description"
              title={t('shared.exposure.situationOrStimulusDescription')}
              render={(value, record) => {
                if (isEditing(record._id)) {
                  return (
                    <Form.Item
                      name={['description']}
                      style={{ margin: 0, minWidth: 120 }}
                    >
                      <Input.TextArea
                        data-testid={`cc-create-exposure-description`}
                        autoSize={{ minRows: 4 }}
                        placeholder={t(
                          'shared.exposure.situationOrStimulusDescriptionPlaceholder',
                        )}
                      />
                    </Form.Item>
                  );
                }
                return value;
              }}
            />

            <Table.Column<ExposureStimulus>
              key="sud"
              dataIndex="sud"
              title={
                <Tooltip title={t('shared.exposure.sudTip')}>
                  {t('shared.exposure.sud')}
                  <QuestionCircleOutlined style={{ margin: '0 10px' }} />
                </Tooltip>
              }
              render={(value, record) => {
                if (isEditing(record._id)) {
                  return (
                    <Form.Item name={['sud']} style={{ margin: 0 }}>
                      <InputNumber
                        min={0}
                        max={100}
                        data-testid={`cc-create-exposure-sud`}
                      />
                    </Form.Item>
                  );
                }
                return value;
              }}
            />

            <Table.Column<ExposureStimulus>
              key="avoidance"
              dataIndex="avoidance"
              title={
                <Tooltip title={t('shared.exposure.doYouAvoid')}>
                  {t('shared.exposure.avoiding')}
                  <QuestionCircleOutlined style={{ margin: '0 10px' }} />
                </Tooltip>
              }
              render={(value, record) => {
                if (isEditing(record._id)) {
                  return (
                    <Form.Item
                      name={'avoidance'}
                      style={{ margin: 0, minWidth: 120 }}
                    >
                      <Select
                        data-testid={`cc-create-exposure-avoidance`}
                        className={styles['exposure--client-selector']}
                        placeholder={t('shared.exposure.avoiding')}
                        optionFilterProp="children"
                        dropdownStyle={{ minWidth: 240 }}
                      >
                        {memoizedAvoidanceOptions}
                      </Select>
                    </Form.Item>
                  );
                }
                return record.avoidance !== null
                  ? t('shared.exposure.avoidances', {
                      returnObjects: true,
                      // @ts-ignore
                    })?.[record.avoidance] || null
                  : null;
              }}
            />

            <Table.Column<ExposureStimulus>
              title={t('resourcesCommon.actions')}
              dataIndex="actions"
              width={60}
              render={(_, record) => {
                // ONE edit gate for all three exposure screens (esHelpers) — the list, this table
                // and the situation editor used to answer it three times, inline, and that is the
                // shape that let E02's surfaces disagree about who may be edited
                const gate = canEditExposure(
                  clients.find((c) => c._id === clientId),
                  {
                    isTherapistAdmin:
                      identity?.db?.role === UserRole.THERAPIST_ADMIN,
                  },
                );
                // the therapist's 2-hour window, in ONE place — the server enforces it in BOTH
                // eras, so a disabled button here is an affordance and never the guard
                const withinDeleteWindow = deleteWindowOpen(record?.createdAt);

                if (isEditing(record._id)) {
                  return (
                    <Space>
                      <SaveButton
                        {...saveButtonProps}
                        onClick={() => {
                          therapistActivitySend({
                            type: 'UPDATED',
                            resource: exposureId,
                          });
                          saveButtonProps.onClick?.();
                        }}
                        size="small"
                        data-testid="save-exposure-situation"
                      />

                      <Button
                        {...cancelButtonProps}
                        size="small"
                        data-testid="cancel-exposure-situation"
                      >
                        {t('buttons.cancel')}
                      </Button>
                    </Space>
                  );
                }
                return (
                  <Space>
                    {
                      <>
                        <Tooltip
                          title={t('shared.exposure.editTitleAndDescription')}
                        >
                          <EditButton
                            {...editButtonProps(record._id)}
                            hideText
                            size="small"
                            disabled={!gate.enabled}
                            resource="exposureStimulus"
                            data-testid="cc-edit-exposure-on-table"
                          />
                        </Tooltip>

                        <Link
                          to={`/tools/exposures/edit?exposureStimulusId=${record._id}`}
                        >
                          <Button
                            icon={<LineChartOutlined />}
                            data-testid="cc-edit-exposure-situation"
                          />
                        </Link>
                      </>
                    }

                    <Tooltip
                      title={t('shared.exposure.deleteAvailable120Minutes')}
                      placement="bottom"
                    >
                      <Popconfirm
                        okText={t('buttons.delete', 'Delete')}
                        cancelText={t('buttons.cancel', 'Cancel')}
                        okType="danger"
                        title={t('buttons.confirm', 'Are you sure?')}
                        onConfirm={() => onDeleteExposureStimulus(record._id)}
                        disabled={!gate.enabled || !withinDeleteWindow}
                      >
                        <Button
                          data-testid="cc-delete-exposure-situation"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={!gate.enabled || !withinDeleteWindow}
                        />
                      </Popconfirm>
                    </Tooltip>
                  </Space>
                );
              }}
            />
          </Table>
        </Form>
      )}
    </Create>
  );
};
